import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { getApiScopes, getMsalInstance, startLogin } from "@/auth/msal";
import { stopViewAs, VIEW_AS_HEADER, viewAsHeader } from "@/auth/view-as";

/** Sent by the API when it rejects the `X-Pulse-View-As` header itself. */
const VIEW_AS_REJECTED_HEADER = "X-Pulse-View-As-Rejected";
import { API_BASE_URL } from "@/lib/env";

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/** The API's answer while the auto-paused database resumes (503 `database_waking`); retrying shortly succeeds. */
export function isDatabaseWaking(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 503) return false;
  const body = error.body;
  return !!body && typeof body === "object" && "code" in body && body.code === "database_waking";
}

/** Acquire an access token for the Pulse API. Falls back to a redirect when interaction is required. */
export async function getAccessToken(): Promise<string> {
  const msal = getMsalInstance();
  if (!msal) throw new Error("Authentication has not been initialised.");
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  const scopes = getApiScopes();
  if (!account) {
    startLogin();
    throw new Error("Redirecting to sign in…");
  }
  try {
    const result = await msal.acquireTokenSilent({ scopes, account });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msal.acquireTokenRedirect({ scopes, account });
      throw new Error("Redirecting to sign in…");
    }
    throw error;
  }
}

export interface ApiFetchOptions extends Omit<RequestInit, "body"> {
  /** JSON-serialisable body. Use `rawBody` for FormData / Blob. */
  body?: unknown;
  rawBody?: BodyInit;
  /** Query string parameters; undefined/null values are dropped. */
  query?: Record<string, string | number | boolean | null | undefined>;
}

function buildUrl(path: string, query?: ApiFetchOptions["query"]): string {
  const url = /^https?:\/\//.test(path) ? path : `${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}${url.includes("?") ? "&" : "?"}${qs}` : url;
}

/** A read with no answer by then is abandoned (and retried by React Query) rather than leaving the screen loading. */
export const READ_TIMEOUT_MS = 45_000;

/** Run `run` with a signal that aborts when the caller's does or after `ms` (null: no limit); a timeout throws a 504 ApiError. */
async function withTimeout<T>(ms: number | null, outer: AbortSignal | null | undefined, run: (signal?: AbortSignal) => Promise<T>): Promise<T> {
  if (ms === null) return run(outer ?? undefined);
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, ms);
  const forward = () => controller.abort();
  if (outer?.aborted) controller.abort();
  outer?.addEventListener("abort", forward);
  try {
    return await run(controller.signal);
  } catch (error) {
    if (timedOut) throw new ApiError(504, "Pulse took too long to respond. Trying again…", null);
    throw error;
  } finally {
    clearTimeout(timer);
    outer?.removeEventListener("abort", forward);
  }
}

/**
 * Authenticated fetch against the Pulse API. Paths are relative to VITE_API_BASE_URL,
 * e.g. apiFetch<Me>("/me"). Throws ApiError on non-2xx responses.
 */
export async function apiFetch<T = unknown>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { body, rawBody, query, headers, ...init } = options;
  const token = await getAccessToken();
  const finalHeaders = new Headers(headers);
  finalHeaders.set("Authorization", `Bearer ${token}`);
  finalHeaders.set("Accept", "application/json");
  const viewAs = viewAsHeader();
  if (viewAs) finalHeaders.set(VIEW_AS_HEADER, viewAs);
  let requestBody: BodyInit | undefined = rawBody;
  if (body !== undefined) {
    finalHeaders.set("Content-Type", "application/json");
    requestBody = JSON.stringify(body);
  }

  const method = (init.method ?? "GET").toUpperCase();
  const response = await withTimeout(method === "GET" ? READ_TIMEOUT_MS : null, init.signal, (signal) =>
    fetch(buildUrl(path, query), { ...init, headers: finalHeaders, body: requestBody, signal }),
  );

  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get("content-type") ?? "";
  const payload: unknown = contentType.includes("application/json")
    ? await response.json().catch(() => null)
    : await response.text().catch(() => null);

  if (!response.ok) {
    const detail =
      payload && typeof payload === "object" && "detail" in payload
        ? (payload as { detail: unknown }).detail
        : undefined;
    // Validation problems (e.g. a business rule: "Solution Provided is required") come as a list of { msg }.
    const listed = Array.isArray(detail)
      ? detail.map((d) => (d && typeof d === "object" && "msg" in d ? String((d as { msg: unknown }).msg) : "")).filter(Boolean)
      : [];
    const message =
      typeof detail === "string"
        ? detail
        : listed.length
          ? listed.join(" · ")
          : `Request failed (${response.status} ${response.statusText})`;
    if (viewAs && response.headers.get(VIEW_AS_REJECTED_HEADER)) {
      // The server refused the preview itself (not an admin any more, production, user deleted): go back to
      // the user's own access rather than leaving every screen failing.
      stopViewAs();
    }
    throw new ApiError(response.status, message, payload);
  }
  return payload as T;
}

/** Authenticated download of a file from the Pulse API (e.g. an invoice PDF). Throws ApiError on non-2xx. */
export async function apiBlob(path: string): Promise<Blob> {
  const token = await getAccessToken();
  const headers = new Headers({ Authorization: `Bearer ${token}` });
  const viewAs = viewAsHeader();
  if (viewAs) headers.set(VIEW_AS_HEADER, viewAs);
  const response = await fetch(buildUrl(path), { headers });
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    const detail = payload && typeof payload === "object" && "detail" in payload ? (payload as { detail: unknown }).detail : undefined;
    throw new ApiError(response.status, typeof detail === "string" ? detail : `Request failed (${response.status})`, payload);
  }
  return response.blob();
}
